/**
 * IrisLoader - Animated loading component featuring the Iris logo
 * Themed around the swift, golden-winged divine messenger
 */

interface IrisLoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  message?: string;
}

export default function IrisLoader({ size = 'md', message }: IrisLoaderProps) {
  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
    xl: 'w-48 h-48',
  };

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      {/* Animated Iris Logo */}
      <div className={`${sizeClasses[size]} relative`}>
        {/* Outer rainbow glow ring */}
        <div className="absolute inset-0 rounded-full animate-spin-slow">
          <div
            className="w-full h-full rounded-full opacity-50"
            style={{
              background:
                'conic-gradient(from 0deg, var(--brand-secondary), var(--brand-outline), var(--brand-tertiary), var(--color-success), var(--brand-primary), var(--brand-secondary))',
              filter: 'blur(8px)',
            }}
          />
        </div>

        {/* Middle golden pulse ring */}
        <div className="absolute inset-2 rounded-full animate-pulse-slow">
          <div
            className="w-full h-full rounded-full opacity-40"
            style={{
              background:
                'radial-gradient(circle, var(--brand-primary) 0%, var(--brand-primary) 50%, transparent 70%)',
              filter: 'blur(4px)',
            }}
          />
        </div>

        {/* Logo container with float animation */}
        <div className="absolute inset-4 animate-float">
          <img
            src="/irus-logo-color.svg"
            alt="Iris Logo"
            className="w-full h-full object-contain animate-shimmer"
          />
        </div>
      </div>

      {/* Loading message */}
      {message && (
        <div className="text-center">
          <p className="text-brand-on-primary font-medium text-lg animate-pulse">{message}</p>
          <div className="flex gap-1 justify-center mt-2">
            <span
              className="w-2 h-2 bg-brand-primary rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 bg-brand-tertiary rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-2 h-2 bg-brand-secondary rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
